import { format as dateFormat, parseISO } from 'date-fns';
import { fields } from 'components/explore/resultsGrid/gridHelpers/fields';

export const isDateOrDateTime = value => {
  const regex = /^(\d{4}-[0-1]\d-[0-3]\d)T([0-2]\d:[0-5]\d:[0-5]\d)\.\d+Z?$/gm;
  return regex.test(value);
}

// all operations which accessable for field types
const filterOperations = {
  number: [
    'anyof',
    'noneof',
    '=',
    '<>',
    '<',
    '>',
    '<=',
    '>=',
    'isblank',
    'isnotblank',
    'between',
    'in',
    'notin'
  ],
  numberWithoutAnyOf: [
    '=',
    '<>',
    '<',
    '>',
    '<=',
    '>=',
    'isblank',
    'isnotblank',
    'between',
    'in',
    'notin'
  ],
  string: [
    'anyof',
    'noneof',
    '=',
    '<>',
    'contains',
    'notcontains',
    'startswith',
    'endswith',
    'isblank',
    'isnotblank',
    'in',
    'notin'
  ],
  stringWithoutAnyOf: [
    '=',
    '<>',
    'contains',
    'notcontains',
    'startswith',
    'endswith',
    'isblank',
    'isnotblank',
    'in',
    'notin'
  ],
  boolean: [
    '=',
    '<>',
    'isblank',
    'isnotblank',
    'anyof',
    'noneof',
  ],
  booleanWithoutAnyOf: [
    '=',
    '<>',
    'isblank',
    'isnotblank'
  ],
  date: [
    '=',
    '<>',
    '<',
    '>',
    '<=',
    '>=',
    'isblank',
    'isnotblank',
    'between'
  ]
}

// fields which available in FilterBuilder
export const filterBuilderFields = fields
  .filter(field => field.filterBuilder?.enabled)
  .map(field => {
    const operatorGroup = field.filterBuilder.disableAnyOf ? field.dataType + 'WithoutAnyOf' : field.dataType;
    const newField = {
      dataField: field.dataField,
      dataType: field.dataType,
      caption: field.caption,
      format: field.filterBuilder?.format,
      filterOperations: filterOperations[operatorGroup] ?? undefined,
    }
    return newField;
  });

function getFieldType(dataField) {
  const fieldObj = filterBuilderFields.find(field => field.dataField === dataField);
  return fieldObj?.dataType;
}

const numberType = 'number';
const dateType = 'date';
const stringType = 'string';
const booleanType = 'boolean';

const equal = 'equal';
const equalOperator = '=';
const greaterThanOrEqual = 'greaterThanOrEqual';
const greaterThanOrEqualOperator = '>=';
const greaterThan = 'greaterThan';
const greaterThanOperator = '>';
const lessThan = 'lessThan';
const lessThanOperator = '<';
const lessThanOrEqual = 'lessThanOrEqual';
const lessThanOrEqualOperator = '<=';
const notEqual = 'notequal';
const notEqualOperator = '!=';
const inOperator = 'in';
const notin = 'notin';
const isNull = 'isnull';
const isNotNull = 'isnotnull';
const startsWith = 'startswith';
const endsWith = 'endswith';
const contains = 'contains';
const isEmpty = 'isempty';
const isNotEmpty = 'isnotempty';
const between = 'between';
const notBetween = 'notbetween';

export const operators = [{
  name: '=',
  caption: 'Equals'
}, {
  name: '<>',
  caption: 'Not Equal'
}, {
  name: '<',
  caption: 'Less than'
}, {
  name: '>',
  caption: 'Greater than'
}, {
  name: '<=',
  caption: 'Less than or equal'
}, {
  name: '>=',
  caption: 'Greater than or equal'
}, {
  name: 'between',
  caption: 'Between'
}, {
  name: 'notbetween',
  caption: 'Not between'
}, {
  name: 'anyof',
  caption: 'Is any of'
}, {
  name: 'noneof',
  caption: 'Is none of'
}, {
  name: 'in',
  caption: 'Is any of (paste)'
}, {
  name: 'notin',
  caption: 'Is none of (paste)'
}, {
  name: 'contains',
  caption: 'Contains'
}, {
  name: 'startswith',
  caption: 'Starts with'
}, {
  name: 'endswith',
  caption: 'Ends with'
}]

export const customOperations = () => {
  return [
    { name: inOperator, caption: 'Is any of (paste)', dataTypes: ['string', 'number'],
      calculateFilterExpression: (filterValue, field) => {
        const filter = [];
        filterValue.forEach(value => {
          filter.push([field.dataField, '=', value]);
          filter.push('or');
        });
        filter.pop();
        return filter;
      }
     },
    { name: notin, caption: 'Is none of (paste)', dataTypes: ['string', 'number'],
      calculateFilterExpression: (filterValue, field) => {
        const filter = [];
        filterValue.forEach(value => {
          filter.push([field.dataField, '=', value]);
          filter.push('or');
        });
        filter.pop();
        return ["!", filter];
      }
     }
  ];
};

export function setInOperatorTextBoxValue(editorEvent) {
  if (['in', 'notin'].includes(editorEvent.filterOperation)) {
    editorEvent.editorName = 'dxTextBox';
    editorEvent.editorOptions.onValueChanged = valueChangedEvent => {
      const parsed = valueChangedEvent.value.split(/[\s,]+/).filter(el => {
        if (editorEvent.dataType === 'string') return true;
        if (!el && el !== 0) return false;
        return !isNaN(Number(el));
      }).map(el => {
        if (editorEvent.dataType === 'string') return el;
        return parseFloat(el);
      })
      editorEvent.setValue(parsed);
    }
  }
}

function getIsNullExpression(field) {
  return `${field} IS NULL`;
}

function getIsNotNullExpression(field) {
  return `${field} IS NOT NULL`;
}

function convertNumberExpression(filter) {
  switch (filter[1]) {
    case equalOperator:
      return `${filter[0]} ${equalOperator} ${filter[2]}`;
    case '<>':
      return `${filter[0]} ${notEqualOperator} ${filter[2]}`;
    case lessThanOperator:
      return `${filter[0]} ${lessThanOperator} ${filter[2]}`;  
    case greaterThanOperator:
      return `${filter[0]} ${greaterThanOperator} ${filter[2]}`;  
    case lessThanOrEqualOperator:
      return `${filter[0]} ${lessThanOrEqualOperator} ${filter[2]}`;  
    case greaterThanOrEqualOperator:
      return `${filter[0]} ${greaterThanOrEqualOperator} ${filter[2]}`;
    case 'isblank':
      return getIsNullExpression(filter[0]);
    case 'isnotblank':
      return getIsNotNullExpression(filter[0]);
    case between:
      return `${filter[0]} BETWEEN ${filter[2][0]} AND ${filter[2][1]}`;
    case 'anyof':
      if (filter[2].length > 1) {
        return `${filter[0]} IN (${filter[2].map(el => {
          if (el === null) return 'Null';
          return el;
        }).join(', ')})`;
      }
      return `${filter[0]} IN (${filter[2][0]})`;
    case 'noneof':
      if (filter[2].length > 1) {
        return `${filter[0]} NOT IN (${filter[2].map(el => {
          if (el === null) return 'Null';
          return el;
        }).join(', ')})`;
      }
      return `${filter[0]} NOT IN (${filter[2][0]})`; 
    case inOperator:
      if (Array.isArray(filter[2])) {
        return `${filter[0]} IN (${filter[2].join(', ')})`;
      }
      return `${filter[0]} IN (${filter[2]})`;
    case notin:
      if (Array.isArray(filter[2])) {
        return `${filter[0]} NOT IN (${filter[2].join(', ')})`;
      }
      return `${filter[0]} NOT IN (${filter[2]})`;
    default: return filter;
  }
}

function convertDateExpression(filter) {
  const formatDate = (date) => {
    if (!date) return 'Null';
    let newDate = date;
    if (typeof date === 'string') newDate = parseISO(date);
    return dateFormat(newDate, 'MM/dd/yyyy');
  }
  const value = Array.isArray(filter[2]) ? filter[2].map(el => formatDate(el)) : formatDate(filter[2]);
  
  switch (filter[1]) {
    case equalOperator:
      return `${filter[0]} ${equalOperator} '${value}'`;
    case '<>':
      return `${filter[0]} ${notEqualOperator} '${value}'`;
    case lessThanOperator:
      return `${filter[0]} ${lessThanOperator} '${value}'`;  
    case greaterThanOperator:
      return `${filter[0]} ${greaterThanOperator} '${value}'`;  
    case lessThanOrEqualOperator:
      return `${filter[0]} ${lessThanOrEqualOperator} '${value}'`;  
    case greaterThanOrEqualOperator:
      return `${filter[0]} ${greaterThanOrEqualOperator} '${value}'`;
    case 'isblank':
      return getIsNullExpression(filter[0]);
    case 'isnotblank':
      return getIsNotNullExpression(filter[0]);
    case between:
      return `${filter[0]} BETWEEN ${value[0]} AND ${value[1]}`;
    case 'anyof':
      if (filter[2].length > 1) {
        return `${filter[0]} IN (${value.join(', ')})`;
      }
      return `${filter[0]} IN (${value[0]})`;
    case 'noneof':
      if (filter[2].length > 1) {
        return `${filter[0]} NOT IN (${value.join(', ')})`;
      }
      return `${filter[0]} NOT IN (${value[0]})`; 
    default: return filter;
  }
}

function convertStringExpression(filter) {
  switch (filter[1]) {
    case equalOperator:
      return `${filter[0]} ${equalOperator} '${filter[2]}'`;
    case '<>':
      return `${filter[0]} ${notEqualOperator} '${filter[2]}'`;
    case contains:
      return `${filter[0]} LIKE ('%${filter[2]}%')`;  
    case 'notcontains':
      return `${filter[0]} NOT LIKE ('%${filter[2]}%')`;  
    case startsWith:
      return `${filter[0]} LIKE ('${filter[2]}%')`;
    case endsWith:
      return `${filter[0]} LIKE ('%${filter[2]}')`;
    case 'isblank':
      return getIsNullExpression(filter[0]);
    case 'isnotblank':
      return getIsNotNullExpression(filter[0]);
    case 'anyof':
      if (filter[2].length > 1) {
        return `${filter[0]} IN (${filter[2].map(el => {
          if (el === null) return 'Null';
          return el;
        }).join(', ')})`;
      }
      return `${filter[0]} IN (${filter[2][0]})`;
    case 'noneof':
      if (filter[2].length > 1) {
        return `${filter[0]} NOT IN (${filter[2].map(el => {
          if (el === null) return 'Null';
          return el;
        }).join(', ')})`;
      }
      return `${filter[0]} NOT IN (${filter[2][0]})`;  
    case inOperator:
      let valueIn = filter[2];
      if (Array.isArray(filter[2])) {
        valueIn = filter[2].join(', ');
      }
      valueIn = valueIn.replaceAll(',', "','");
      return `${filter[0]} IN ('${valueIn}')`;
    case notin:
      let valueNotIn = filter[2];
      if (Array.isArray(filter[2])) {
        valueNotIn = filter[2].join(', ');
      }
      valueNotIn = valueNotIn.replaceAll(',', "','");
      return `${filter[0]} NOT IN ('${valueNotIn}')`;
    default: return filter;
  }
}

function convertBooleanExpression(filter) {
  switch (filter[1]) {
    case equalOperator:
      return `${filter[0]} ${equalOperator} '${filter[2] ? 'True' : 'False'}'`;
    case '<>':
      return `${filter[0]} ${notEqualOperator} '${filter[2] ? 'True' : 'False'}'`;
    case 'isblank':
      return getIsNullExpression(filter[0]);
    case 'isnotblank':
      return getIsNotNullExpression(filter[0]);
    case 'anyof':
      if (filter[2].length > 1) {
        return `${filter[0]} IN (${filter[2].map(el => {
          if (el === null) return 'Null';
          return el;
        }).join(', ')})`;
      }
      return `${filter[0]} IN (${filter[2][0]})`; 
    case 'noneof':
      if (filter[2].length > 1) {
        return `${filter[0]} NOT IN (${filter[2].map(el => {
          if (el === null) return 'Null';
          return el;
        }).join(', ')})`;
      }
      return `${filter[0]} NOT IN (${filter[2][0]})`; 
    default: return filter;
  }
}

export function convertFilterToSql(filter) {
  if (!filter) return filter;
  const isLowLevelFilter = checkIsLowLevelFilter(filter);
  if (!isLowLevelFilter) return filter;

  let filterString;

  const dataType = getFieldType(filter[0]);
  switch (dataType) {
    case numberType:
      filterString = convertNumberExpression(filter);
      break;
    case dateType:
      filterString = convertDateExpression(filter);
      break;
    case stringType:
      filterString = convertStringExpression(filter);
      break;
    case booleanType:
      filterString = convertBooleanExpression(filter);
      break;
    default: break;
  }

  return filterString;
}

function checkIsLowLevelFilter(filter) {
  const isOrNotBetween = filter?.[1] === between || filter?.[1] === notBetween;
  const isOrNotIn = (filter?.[1] === inOperator || filter?.[1] === notin)
    && (Array.isArray(filter?.[2]) || typeof filter?.[2] === 'string');
  const isAnyOfOrNoneOf = (filter?.[1] === 'anyof' || filter?.[1] === 'noneof')
    && (Array.isArray(filter?.[2]) || typeof filter?.[2] === 'string');

  const isLowLevelFilter =
    Array.isArray(filter)
    && (isOrNotBetween || isOrNotIn || isAnyOfOrNoneOf
      || (!Array.isArray(filter?.[0])
        && !Array.isArray(filter?.[1])
        && !Array.isArray(filter?.[2])));
  return isLowLevelFilter;
}

export function getWholeFilter(filter) {
  if (!filter) return;
  let finalFilter = '';

  const isLowLevelFilter = checkIsLowLevelFilter(filter);
  if (isLowLevelFilter) {
    finalFilter += convertFilterToSql(filter);
    return finalFilter;
  }

  filter.forEach(item => {
    const isLowLevelFilter = checkIsLowLevelFilter(item);
    if (isLowLevelFilter) {
      finalFilter += convertFilterToSql(item);
    } else {
      if (Array.isArray(item?.[0]) || Array.isArray(item?.[2])) {
        finalFilter += '(';
        finalFilter += getWholeFilter(item);
        finalFilter += ')';
      }
    }

    if (item === 'and') {
      finalFilter += ' AND ';
    }

    if (item === 'or') {
      finalFilter += ' OR ';
    }
  });

  return finalFilter;
}

function convertCustomOperatorToDefault(filter, getRawExpression) {
  try {
    if (!filter) return filter;
    const isLowLevelFilter = checkIsLowLevelFilter(filter);
    if (!isLowLevelFilter) return filter;

    const convertDates = input => {
      if (typeof input === 'string' && isDateOrDateTime(input)) {
        return dateFormat(parseISO(input), "yyyy-MM-dd'T'HH:mm:ss.SSS");
      }
      return input;
    }

    const getNewFilter = () => {
      const newFilter = [...filter];
      switch (filter[1]) {
        case equal:
          newFilter[1] = equalOperator;
          newFilter[2] = convertDates(newFilter[2]);
          return newFilter;
        case notEqual:
          newFilter[1] = '<>';
          newFilter[2] = convertDates(newFilter[2]);
          return newFilter;
        case lessThan:
          newFilter[1] = lessThanOperator;
          newFilter[2] = convertDates(newFilter[2]);
          return newFilter;
        case greaterThan:
          newFilter[1] = greaterThanOperator;
          newFilter[2] = convertDates(newFilter[2]);
          return newFilter;
        case lessThanOrEqual:
          newFilter[1] = lessThanOrEqualOperator;
          newFilter[2] = convertDates(newFilter[2]);
          return newFilter;
        case greaterThanOrEqual:
          newFilter[1] = greaterThanOrEqualOperator;
          newFilter[2] = convertDates(newFilter[2]);
          return newFilter;
        case isEmpty:  
        case isNull:
          return [filter[0], equalOperator, null];
        case isNotEmpty:  
        case isNotNull:
          return [filter[0], '<>', null];
        case between:
          if (!getRawExpression) {
            newFilter[2] = [convertDates(newFilter[2][0]), convertDates(newFilter[2][1])];
            return newFilter;
          }
          return [[filter[0], greaterThanOrEqualOperator, convertDates(filter[2][0])], 'and', [filter[0], lessThanOrEqualOperator, convertDates(filter[2][1])]];
        case notBetween:
          if (!getRawExpression) {
            newFilter[2] = [convertDates(newFilter[2][0]), convertDates(newFilter[2][1])];
            return newFilter;
          }
          return [[filter[0], lessThanOperator, convertDates(filter[2][0])], 'or', [filter[0], greaterThanOperator, convertDates(filter[2][1])]];
        case inOperator:
          if (!Array.isArray(newFilter[2])) {
            const dataType = filterBuilderFields.find(field => field.dataField === filter[0])?.dataType;
            newFilter[2] = newFilter[2].split(/[\s,]+/).filter(el => {
              if (dataType === 'string') return true;
              if (!el && el !== 0) return false;
              return !isNaN(Number(el));
            }).map(el => {
              if (dataType === 'string') return el;
              return parseFloat(el);
            });
          } 
          if (getRawExpression) {
            const arr = [];
            newFilter[2].forEach(val => {
              arr.push([filter[0], equalOperator, val]);
              arr.push('or');
            });
            arr.pop();
            return arr;
          }
          return newFilter;
        case notin:
          if (!Array.isArray(newFilter[2])) {
            const dataType = filterBuilderFields.find(field => field.dataField === filter[0])?.dataType;
            newFilter[2] = newFilter[2].split(/[\s,]+/).filter(el => {
              if (dataType === 'string') return true;
              if (!el && el !== 0) return false;
              return !isNaN(Number(el));
            }).map(el => {
              if (dataType === 'string') return el;
              return parseFloat(el);
            });
          } 
          if (getRawExpression) {
            const arr = [];
            newFilter[2].forEach(val => {
              arr.push([filter[0], '<>', val]);
              arr.push('and');
            });
            arr.pop();
            return arr;
          }
          return newFilter;
        case contains:
        case startsWith:
        case endsWith:
        default: return newFilter;
      }
    }
    return getNewFilter();
  }  
  catch(e) {
    console.log(e)
    console.log(filter)
  }
}

export function customOperatorToDefault(filter, getRawExpression) {
  if (!filter) return;
  let newFilter = [...filter];

  const isLowLevelFilter = checkIsLowLevelFilter(filter);
  if (isLowLevelFilter) {
    newFilter = convertCustomOperatorToDefault(filter, getRawExpression);
    return newFilter;
  }

  newFilter = newFilter.map(item => {
    const isLowLevelFilter = checkIsLowLevelFilter(item);
    let newItem;
    if (isLowLevelFilter) {
      newItem = convertCustomOperatorToDefault(item, getRawExpression);
    } else {
      if (Array.isArray(item?.[0]) || Array.isArray(item?.[2])) {
        newItem = customOperatorToDefault(item, getRawExpression);
      } else {
        newItem = item;
      }
    }
    return newItem;
  });

  return newFilter;
}